// src/pages/Auth/components/PasswordStep.js

import React, { useState } from 'react';
import { Box, Link, IconButton, Typography, CircularProgress, Button } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LOADING_STATES } from '../constants';
import { StyledInput, StyledButton } from '../styles/Styles';

const PasswordStep = ({ 
  email, 
  password, 
  setPassword, 
  rememberMe, 
  setRememberMe,
  loadingState, 
  onSubmit, 
  onForgotClick,
  onBack,
  isDarkMode 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isLoading = loadingState === LOADING_STATES.PASSWORD;

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit(e);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box>
        <Box 
          sx={{ 
            mb: 3,
            p: 2,
            backgroundColor: isDarkMode ? "#0a1020" : "#f0fdfa",
            borderRadius: "10px",
            border: `1px solid ${isDarkMode ? "#2d2d3f" : "#ccfbf1"}`,
          }}
        >
          <Typography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary", fontSize: "14px", mb: 1 }}>
            Welcome back! Logging in as
          </Typography>
          <Typography variant="body1" fontWeight="600" sx={{ color: "#219297" }}>
            {email}
          </Typography>
        </Box>
        
        <Box mb={2} sx={{ position: "relative" }}>
          <Typography 
            component="label" 
            htmlFor="login-password"
            sx={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Password
          </Typography>
          <StyledInput
            id="login-password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Enter your password"
            isDarkMode={isDarkMode}
            sx={{ paddingRight: "40px" }}
          />
          <IconButton
            onClick={() => setShowPassword(!showPassword)}
            sx={{
              position: "absolute",
              right: "8px",
              top: "34px",
              padding: "8px",
            }}
          >
            {showPassword ? 
              <VisibilityOff fontSize="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined }} /> : 
              <Visibility fontSize="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined }} />
            }
          </IconButton>
        </Box>

        <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{
                width: "16px",
                height: "16px",
                marginRight: "8px",
                cursor: "pointer",
                accentColor: "#219297",
              }}
            />
            <Typography 
              component="label" 
              htmlFor="rememberMe"
              sx={{ 
                fontSize: "13px", 
                color: isDarkMode ? "#e2e8f0" : "#374151",
                cursor: "pointer",
                userSelect: "none"
              }}
            >
              Keep me signed in
            </Typography>
          </Box>
            
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={onForgotClick}
            sx={{ 
              color: "#219297", 
              fontWeight: 500,
              fontSize: "14px",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              }
            }}
          >
            Forgot password?
          </Link>
        </Box>

        <StyledButton
          type="submit"
          variant="contained"
          fullWidth
          disabled={isLoading}
          isDarkMode={isDarkMode}
        >
          {isLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
              <CircularProgress size={20} sx={{ color: "white" }} />
              <Typography sx={{ color: "white" }}>Logging in...</Typography>
            </Box>
          ) : (
            "Login"
          )}
        </StyledButton>

       <Button
          variant="text"
          fullWidth
          onClick={onBack}
          sx={{
            mt: 2,
            color: "#219297",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            "&:hover": {
              backgroundColor: isDarkMode ? "#2d2d3f" : "#f0fdfa",
            },
          }}
        >
          ← Change Email Address
        </Button>
      </Box>
    </form>
  );
};

export default React.memo(PasswordStep);